import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { nanoid } from 'nanoid';
import { useDispatch, useSelector } from 'react-redux';

import validationSchema from '../helpers/validationSchema';
import '../assets/styles/form.scss';
import { fileSubmit, formSubmit } from '../redux/slices/formSlice';
import { fetchModulesData } from '../redux/slices/modulesSlice';

const useHandleSubmitForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, success, error, errorMessage } = useSelector((s) => s.form);
  const {
    register,
    handleSubmit,
    watch,
    resetField,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const pageContainerRef = useRef(null);

  const [activeStep, setStep] = useState(1);
  const [projectType, setProjectType] = useState('');
  const [projectDomain, setProjectDomain] = useState('');
  const [isOpenModal, setOpenModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState({});
  const [surveyId, setSurveyId] = useState('');
  const [isSent, setSending] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!loading && isSent) {
      if (success) onRedirectToReport(surveyId);
      if (error) setIsError(true);
    }
  }, [loading, success, isSent, surveyId, error]);

  useEffect(() => {
    if (projectType && projectDomain) {
      const referenceData = { type_id: projectType, domain_id: projectDomain };
      dispatch(fetchModulesData(referenceData));
    }
  }, [projectType, projectDomain]);

  const checkDesign = (list) => {
    const design = list.find((item) => item === 'design');

    if (design) return true;
    return false;
  };

  const onSubmit = (data) => {
    const pageId = nanoid();

    const survey_data = {
      budget: data.budget,
      description: data.description,
      domain_id: data.domain,
      domain_other: data.domain_other,
      have_design: checkDesign(data.stage),
      modules: data.modules,
      page_id: pageId,
      stage: data.stage,
      time_period: data.time_period,
      type_id: data.type,
      name: data.name,
      email: data.email,
    };

    dispatch(formSubmit(survey_data));
    setSurveyId(pageId);
    setSending(true);
  };

  const onRedirectToReport = (id) => {
    if (uploadedFile?.name) {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      dispatch(fileSubmit({ id, formData }));
    }
    if (isOpenModal) setOpenModal(false);
    navigate(`/report/${id}`);
  };

  return useMemo(
    () => ({
      loading,
      isError,
      errorMessage,
      error,
      register,
      handleSubmit,
      watch,
      resetField,
      reset,
      getValues,
      onSubmit,
      formStateErrors: errors,
      pageContainerRef,
      isOpenModal,
      setOpenModal,
      isSent,
      setSending,
      activeStep,
      setStep,
      projectType,
      setProjectType,
      projectDomain,
      setProjectDomain,
      uploadedFile,
      setUploadedFile,
    }),
    [
      loading,
      isSent,
      isError,
      error,
      errorMessage,
      projectType,
      projectDomain,
      uploadedFile,
      activeStep,
      isOpenModal,
      onSubmit,
      errors,
    ],
  );
};

export default useHandleSubmitForm;
